// src/data/languages/peul/sentences.ts

interface Sentence {
  original: string;
  french: string;
  // English translation of `french` — optional, absent today. Falls back to
  // `french` when missing (see src/utils/localizedContent.ts).
  english?: string;
  words: string[];
  audio?: string;
  category: string;
  tags: string[];
}

export const SENTENCE_CATEGORIES = {
  'Salutations': {
    icon: '👋',
    description: 'Premiers contacts et formules de politesse',
  },
  'Présentation': {
    icon: '🤝',
    description: 'Se présenter et faire connaissance',
  },
  'Vie quotidienne': {
    icon: '🌞',
    description: 'Expressions du quotidien',
  },
  'Au marché': {
    icon: '🛍️',
    description: 'Faire ses courses et négocier',
  },
  'Voyages': {
    icon: '✈️',
    description: 'Se déplacer et visiter',
  },
  'Famille et relations': {
    icon: '👨‍👩‍👧‍👦',
    description: 'Parler de sa famille et de ses proches',
  },
  'Urgences et santé': {
    icon: '🏥',
    description: 'Expressions pour les situations médicales et urgentes',
  },
  'Loisirs et culture': {
    icon: '🎭',
    description: 'Parler de ses activités, passe-temps et culture',
  }
} as const;

// Collection de phrases à construire en peul (pulaar)
export const sentencesToConstruct: Sentence[] = [
  // ===== SALUTATIONS =====
  {
    original: "En wadoyay janngo",
    french: "On le fera demain",
    english: "We'll do it tomorrow",
    words: ["En", "wadoyay", "janngo"],
    category: 'Salutations',
    tags: ['futur', 'action']
  },

  {
    original: "Oh Oh",
    french: "Non",
    english: "No",
    words: ["Oh", "Oh"],
    category: 'Salutations',
    tags: ['futur', 'action'],
    audio: 'Oh_oh'
  },
   {
    original: "Hii hi",
    french: "Oui",
    english: "Yes",
    words: ["Hii", "hi"],
    category: 'Salutations',
    tags: ['futur', 'action']
  },
  {
    original: "Mi yahoytaa ton janngo",
    french: "Je n'irai pas là-bas demain",
    english: "I won't go there tomorrow",
    words: ["Mi", "yahoytaa", "ton", "janngo"],
    category: 'Salutations',
    tags: ['négation', 'futur'],
    audio: 'Mi_yahoytaa_ton_janngo'
  },
  {
    original: "Mi hoolaaki mi yahoyayton",
    french: "Je ne suis pas sûr d'y aller",
    english: "I'm not sure I'll go there",
    words: ["Mi", "hoolaaki", "mi", "yahoyayton"],
    category: 'Salutations',
    tags: ['doute', 'incertitude'],
    audio: 'Mi_hoolaaki_mi_yahoyayton'
  },
  {
    original: "Wonaa min tun woni gaa",
    french: "Je ne suis pas seul",
    english: "I'm not alone",
    words: ["Wonaa", "min", "tun", "woni", "gaa"],
    category: 'Salutations',
    tags: ['présence', 'accompagnement'],
    audio: 'Wonaa_min_tun_woni_gaa'
  },
  {
    original: "Mi jommbaali tawo",
    french: "Je ne suis pas encore marié",
    english: "I'm not married yet",
    words: ["Mi", "jommbaali", "tawo"],
    category: 'Présentation',
    tags: ['état civil', 'négation'],
    audio: 'Mi_jommbaali_tawo'
  },
  {
    original: "Ko honno wiyè tè ndha",
    french: "Comment t'appelles tu ?",
    english: "What is your name?",
    words: ["Ko", "honno", "wiyè", "tè", "ndha"],
    category: 'Présentation',
    tags: ['nom', 'question', 'identité'],
    audio: 'Ko_honno_wiye_te_ndha'
  },

  {
    original: "Ko honto hoddhou ndha",
    french: "Où habites-tu ?",
    english: "Where do you live?",
    words: ["Ko", "honto", "hoddhou", "ndha"],
    category: 'Présentation',
    tags: ['lieu', 'habitation', 'question'],
    audio: 'Ko_honto_hoddhou_ndha'
  },
  {
    original: "Ko Médina mi hoddhi",
    french: "J'habite à la Médina",
    english: "I live in Médina",
    words: ["Ko", "Médina", "mi", "hoddhi"],
    category: 'Présentation',
    tags: ['lieu', 'habitation', 'réponse'],
    audio: 'Ko_Medina_mi_hoddhi'
  },
  {
    original: "Ko honto hoddhou ndhon dioni",
    french: "Où habitez-vous maintenant ?",
    english: "Where do you live now?",
    words: ["Ko", "honto", "hoddhou", "ndhon", "dioni"],
    category: 'Présentation',
    tags: ['lieu', 'habitation', 'question', 'pluriel'],
    audio: 'Ko_honto_hoddhou_ndhon_dioni'
  },
  {
    original: "Ko honto hoddhou ndha dioni",
    french: "Où habites-tu maintenant ?",
    english: "Where do you live now?",
    words: ["Ko", "honto", "hoddhou", "ndha", "dioni"],
    category: 'Présentation',
    tags: ['lieu', 'habitation', 'question', 'temps'],
    audio: 'Ko_honto_hoddhou_ndha_dioni'
  },
  {
    original: "Dioni ko Parcelles mi hoddhi",
    french: "Maintenant j'habite aux Parcelles",
    english: "Now I live in Parcelles",
    words: ["Dioni", "ko", "Parcelles", "mi", "hoddhi"],
    category: 'Présentation',
    tags: ['lieu', 'habitation', 'réponse', 'temps'],
    audio: 'Dioni_ko_Parcelles_mi_hoddhi'
  },
  {
    original: "Ko dhoubi diélou mardhaa",
    french: "Quel âge as-tu ?",
    english: "How old are you?",
    words: ["Ko", "dhoubi", "diélou", "mardhaa"],
    category: 'Présentation',
    tags: ['âge', 'question'],
    audio: 'Ko_dhoubi_dielou_mardhaa'
  },
  {
    original: "Ko dhoubi diélou mardhon",
    french: "Quel âge avez-vous ?",
    english: "How old are you?",
    words: ["Ko", "dhoubi", "diélou", "mardhon"],
    category: 'Présentation',
    tags: ['âge', 'question', 'pluriel'],
    audio: 'Ko_dhoubi_dielou_mardhon'
  },
  {
    original: "Hilan mari dhoubi nogaye et diowi",
    french: "J'ai 25 ans",
    english: "I am 25 years old",
    words: ["Hilan", "mari", "dhoubi", "nogaye", "et", "diowi"],
    category: 'Présentation',
    tags: ['âge', 'réponse', 'nombre'],
    audio: 'Hilan_mari_dhoubi_nogaye_et_diowi'
  },
  {
    original: "numéro téléphone ma on ko hon-noun",
    french: "Quel est votre numéro de téléphone ?",
    english: "What is your phone number?",
    words: ["numéro", "téléphone", "ma", "on", "ko", "hon-noun"],
    category: 'Présentation',
    tags: ['contact', 'téléphone', 'question'],
    audio: 'numero_telephone_ma_on_ko_hon_noun'
  },
  {
    original: "Numéro téléphone an on ko ..",
    french: "Mon numéro de téléphone est",
    english: "My phone number is",
    words: ["Numéro", "téléphone", "an", "on", "ko", ".."],
    category: 'Présentation',
    tags: ['contact', 'téléphone', 'réponse'],
    audio: 'Numero_telephone_an_on_ko'
  },
  {
    original: "On djombi",
    french: "Êtes-vous marié ?",
    english: "Are you married?",
    words: ["On", "djombi"],
    category: 'Présentation',
    tags: ['état civil', 'question', 'pluriel'],
    audio: 'On_djombi'
  },
  {
    original: "a djombi",
    french: "Es-tu marié ?",
    english: "Are you married?",
    words: ["a", "djombi"],
    category: 'Présentation',
    tags: ['état civil', 'question'],
    audio: 'a_djombi'
  },

  {
    original: "Ko Siradio Diallo mi wiyètè",
    french: "Je m'appelle Siradio Diallo",
    english: "My name is Siradio Diallo",
    words: ["Ko", "Siradio", "Diallo", "mi", "wiyètè"],
    category: 'Présentation',
    tags: ['nom', 'identité', 'présentation']
  },
  {
    original: "Ko honto hoddhou ndha",
    french: "Où habites-tu ?",
    english: "Where do you live?",
    words: ["Ko", "honto", "hoddhou", "ndha"],
    category: 'Présentation',
    tags: ['lieu', 'habitation', 'question'],
    audio: 'Ko_honto_hoddhou_ndha'
  },
  {
    original: "Ko Médina mi hoddhi",
    french: "J'habite à la Médina",
    english: "I live in Médina",
    words: ["Ko", "Médina", "mi", "hoddhi"],
    category: 'Présentation',
    tags: ['lieu', 'habitation', 'ville'],
    audio: 'Ko_Medina_mi_hoddhi'
  },
  {
    original: "Ko honto hoddhou ndhon dioni",
    french: "Où habitez-vous maintenant ?",
    english: "Where do you live now?",
    words: ["Ko", "honto", "hoddhou", "ndhon", "dioni"],
    category: 'Présentation',
    tags: ['lieu', 'habitation', 'question', 'pluriel'],
    audio: 'Ko_honto_hoddhou_ndhon_dioni'
  },
  {
    original: "Ko honto hoddhou ndha dioni",
    french: "Où habites-tu maintenant ?",
    english: "Where do you live now?",
    words: ["Ko", "honto", "hoddhou", "ndha", "dioni"],
    category: 'Présentation',
    tags: ['lieu', 'habitation', 'question', 'temps'],
    audio: 'Ko_honto_hoddhou_ndha_dioni'
  },
  {
    original: "Dioni ko Parcelles mi hoddhi",
    french: "Maintenant j'habite aux Parcelles",
    english: "Now I live in Parcelles",
    words: ["Dioni", "ko", "Parcelles", "mi", "hoddhi"],
    category: 'Présentation',
    tags: ['lieu', 'habitation', 'quartier'],
    audio: 'Dioni_ko_Parcelles_mi_hoddhi'
  },
  {
    original: "Ko dhoubi diélou mardhaa",
    french: "Quel âge as-tu ?",
    english: "How old are you?",
    words: ["Ko", "dhoubi", "diélou", "mardhaa"],
    category: 'Présentation',
    tags: ['âge', 'question'],
    audio: 'Ko_dhoubi_dielou_mardhaa'
  },
  {
    original: "Ko dhoubi diélou mardhon",
    french: "Quel âge avez-vous ?",
    english: "How old are you?",
    words: ["Ko", "dhoubi", "diélou", "mardhon"],
    category: 'Présentation',
    tags: ['âge', 'question', 'pluriel'],
    audio: 'Ko_dhoubi_dielou_mardhon'
  },
  {
    original: "Hilan mari dhoubi nogaye et diowi",
    french: "J'ai 25 ans",
    english: "I am 25 years old",
    words: ["Hilan", "mari", "dhoubi", "nogaye", "et", "diowi"],
    category: 'Présentation',
    tags: ['âge', 'nombre'],
    audio: 'Hilan_mari_dhoubi_nogaye_et_diowi'
  },
  {
    original: "numéro téléphone ma on ko hon-noun",
    french: "Quel est votre numéro de téléphone ?",
    english: "What is your phone number?",
    words: ["numéro", "téléphone", "ma", "on", "ko", "hon-noun"],
    category: 'Présentation',
    tags: ['contact', 'téléphone', 'question'],
    audio: 'numero_telephone_ma_on_ko_hon_noun'
  },
  {
    original: "Numéro téléphone an on ko ..",
    french: "Mon numéro de téléphone est",
    english: "My phone number is",
    words: ["Numéro", "téléphone", "an", "on", "ko", ".."],
    category: 'Présentation',
    tags: ['contact', 'téléphone'],
    audio: 'Numero_telephone_an_on_ko'
  },
  {
    original: "On djombi",
    french: "Êtes-vous marié ?",
    english: "Are you married?",
    words: ["On", "djombi"],
    category: 'Présentation',
    tags: ['état civil', 'question', 'pluriel'],
    audio: 'On_djombi'
  },
  {
    original: "a djombi",
    french: "Es-tu marié ?",
    english: "Are you married?",
    words: ["a", "djombi"],
    category: 'Présentation',
    tags: ['état civil', 'question'],
    audio: 'a_djombi'
  },
  {
    original: "on djombaama",
    french: "Êtes-vous mariée ?",
    english: "Are you married?",
    words: ["on", "djombaama"],
    category: 'Présentation',
    tags: ['état civil', 'question', 'féminin', 'pluriel'],
    audio: 'on_djombaama'
  },
  {
    original: "a djombaama",
    french: "Es-tu mariée ?",
    english: "Are you married?",
    words: ["a", "djombaama"],
    category: 'Présentation',
    tags: ['état civil', 'question', 'féminin'],
    audio: 'a_djombaama'
  },
  {
    original: "Hidhon mari bhibhè",
    french: "Avez-vous des enfants ?",
    english: "Do you have children?",
    words: ["Hidhon", "mari", "bhibhè"],
    category: 'Présentation',
    tags: ['famille', 'enfants', 'question', 'pluriel'],
    audio: 'Hidhon_mari_bhibhe'
  },
  {
    original: "Ko honno yette tè ndha",
    french: "Quel est ton nom ?",
    english: "What is your name?",
    words: ["Ko", "honno", "yette", "tè", "ndha"],
    category: 'Présentation',
    tags: ['nom', 'question', 'identité'],
    audio: 'Ko_honno_yette_te_ndha'
  },
  {
    original: "Ko hondun habbidaa ?",
    french: "Qu'est-ce que tu attends?",
    english: "What are you waiting for?",
    words: ["Ko", "hondun", "habbidaa","?"],
    category: 'Vie quotidienne',
    tags: ['question', 'attente'],
    audio: 'Ko_hondun_habbidaa'
  },
  {
    original: "Mi hewtii ton jooni",
    french: "J'arrive là-bas tout de suite",
    english: "I'm arriving there right away",
    words: ["Mi", "hewtii", "ton", "jooni"],
    category: 'Vie quotidienne',
    tags: ['arrivée', 'immédiat'],
    audio: 'Mi_hewtii_ton_jooni'
  },
  {
    original: "Hari mi faamaali",
    french: "Je n'avais pas compris",
    english: "I hadn't understood",
    words: ["Hari", "mi", "faamaali"],
    category: 'Vie quotidienne',
    tags: ['compréhension', 'passé', 'négation'],
    audio: 'Hari_mi_faamaali'
  },
  {
    original: "Kono jooni mi faami",
    french: "Mais maintenant j'ai compris",
    english: "But now I understand",
    words: ["Kono", "jooni", "mi", "faami"],
    category: 'Vie quotidienne',
    tags: ['compréhension', 'présent'],
    audio: 'Kono_jooni_mi_faami'
  },
  {
    original: "Awa, moyyi",
    french: "Tant mieux",
    english: "So much the better",
    words: ["Awa", "moyyi"],
    category: 'Vie quotidienne',
    tags: ['satisfaction', 'approbation'],
    audio: 'Awa_moyyi'
  },
  {
    original: "O artii jooni",
    french: "Il vient juste d'arriver",
    english: "He just arrived",
    words: ["O", "artii", "jooni"],
    category: 'Vie quotidienne',
    tags: ['arrivée', 'récent'],
    audio: 'O_artii_jooni'
  },
  {
    original: "O yaltii jooni",
    french: "Il vient juste de sortir",
    english: "He just went out",
    words: ["O", "yaltii", "jooni"],
    category: 'Vie quotidienne',
    tags: ['départ', 'récent']
  },
  {
    original: "Wadii lebbi didi gila mi iwi ton",
    french: "Cela fait deux mois depuis que j'ai quitté là-bas",
    english: "It's been two months since I left there",
    words: ["Wadii", "lebbi", "didi", "gila", "mi", "iwi", "ton"],
    category: 'Voyages',
    tags: ['temps', 'durée', 'départ'],
    audio: 'Wadii_lebbi_didi_gila_mi_iwi_ton'
  },
  {
    original: "Lanndo mo haa tinaa",
    french: "Demande lui jusqu'à comprendre",
    english: "Ask him until you understand",
    words: ["Lanndo", "mo", "haa", "tinaa"],
    category: 'Vie quotidienne',
    tags: ['demande', 'compréhension'],
    audio: 'Lanndo_mo_haa_tinaa'
  },
  {
    original: "mo anndaa lanndoto",
    french: "celui qui ne sait pas demande",
    english: "Whoever doesn't know, asks",
    words: ["mo", "anndaa", "lanndoto"],
    category: 'Vie quotidienne',
    tags: ['savoir', 'question'],
    audio: 'mo_anndaa_lanndoto'
  },
  {
    original: "Firlito mi jeejii ma",
    french: "Répète s'il te plaît",
    english: "Please repeat",
    words: ["Firlito", "mi", "jeejii", "ma"],
    category: 'Vie quotidienne',
    tags: ['répétition', 'demande']
  },
  {
    original: "No waalanii lan",
    french: "Ça me convient",
    english: "That suits me",
    words: ["No", "waalanii", "lan"],
    category: 'Vie quotidienne',
    tags: ['accord', 'satisfaction']
  },
  {
    original: "Waaway wonude ?",
    french: "Est-ce possible?",
    english: "Is it possible?",
    words: ["Waaway", "wonude","?"],
    category: 'Vie quotidienne',
    tags: ['possibilité', 'question'],
    audio: 'Waaway_wonude'
  },
  {
    original: "Mi sikkaa",
    french: "Je ne pense pas",
    english: "I don't think so",
    words: ["Mi", "sikkaa"],
    category: 'Vie quotidienne',
    tags: ['opinion', 'négation'],
    audio: 'Mi_sikkaa'
  },
  {
    original: "Mi sali cut",
    french: "Je refuse catégoriquement",
    english: "I categorically refuse",
    words: ["Mi", "sali", "cut"],
    category: 'Vie quotidienne',
    tags: ['refus', 'fermeté'],
    audio: 'Mi_sali_cut'
  },
  {
    original: "Men yi'idaali tawo",
    french: "Nous ne nous sommes pas vu pour le moment",
    english: "We haven't seen each other yet",
    words: ["Men", "yi'idaali", "tawo"],
    category: 'Salutations',
    tags: ['rencontre', 'négation', 'temps']
  },
  {
    original: "Nde noddudaa lan hari mido daanii",
    french: "Je dormais quand tu m'as appelé",
    english: "I was sleeping when you called me",
    words: ["Nde", "noddudaa", "lan", "hari", "mido", "daanii"],
    category: 'Vie quotidienne',
    tags: ['sommeil', 'passé', 'appel']
  },
  {
    original: "Mi faamaali ko haaludon kon",
    french: "Je n'ai pas compris ce que vous avez dit",
    english: "I didn't understand what you said",
    words: ["Mi", "faamaali", "ko", "haaludon", "kon"],
    category: 'Vie quotidienne',
    tags: ['compréhension', 'négation'],
    audio: 'Mi_faamaali_ko_haaludon_kon'
  },
  {
    original: "En kiikiide",
    french: "À ce soir",
    english: "See you tonight",
    words: ["En", "kiikiide"],
    category: 'Salutations',
    tags: ['au revoir', 'temps', 'soir'],
    audio: 'En_kiikiide'
  },
  {
    original: "En janngo bimmbi",
    french: "À demain matin",
    english: "See you tomorrow morning",
    words: ["En", "janngo", "bimmbi"],
    category: 'Salutations',
    tags: ['au revoir', 'temps', 'matin'],
    audio: 'En_janngo_bimmbi'
  },
  {
    original: "En faddi janngo",
    french: "À après demain",
    english: "See you the day after tomorrow",
    words: ["En", "faddi", "janngo"],
    category: 'Salutations',
    tags: ['au revoir', 'temps', 'futur'],
    audio: 'En_faddi_janngo'
  },
  {
    original: "En jooni",
    french: "À tout de suite",
    english: "See you in a moment",
    words: ["En", "jooni"],
    category: 'Salutations',
    tags: ['au revoir', 'immédiat'],
    audio: 'En_jooni'
  },
  {
    original: "En si burii doo",
    french: "À plus tard",
    english: "See you later",
    words: ["En", "si", "burii", "doo"],
    category: 'Salutations',
    tags: ['au revoir', 'temps']
  },
  {
    original: "Mi soodii comci kesi hanki ka luumo",
    french: "J'ai acheté de nouveaux habits hier au marché",
    english: "I bought new clothes yesterday at the market",
    words: ["Mi", "soodii", "comci", "kesi", "hanki", "ka", "luumo"],
    category: 'Au marché',
    tags: ['achat', 'vêtements', 'passé']
  },
  {
    original: "Ko djélou",
    french: "C'est combien ?",
    english: "How much is it?",
    words: ["Ko", "djélou"],
    category: 'Au marché',
    tags: ['prix', 'question', 'commerce'],
    audio: 'Ko_djelou'
  },
  {
    original: "be hewtaali tawo",
    french: "Je n'ai pas compris ce que vous avez dit",
    english: "I didn't understand what you said",
    words: ["be", "hewtaali", "tawo"],
    category: 'Vie quotidienne',
    tags: ['compréhension', 'négation'],
    audio: 'be_hewtaali_tawo'
  },
  {
    original: "Bee mi habboo be",
    french: "Il faut que je les attende",
    english: "I have to wait for them",
    words: ["Bee", "mi", "habboo", "be"],
    category: 'Vie quotidienne',
    tags: ['obligation', 'attente'],
    audio: 'Bee_mi_habboo_be'
  },
  {
    original: "Mido nawni",
    french: "Je suis malade",
    english: "I am sick",
    words: ["Mido", "nawni"],
    category: 'Urgences et santé',
    tags: ['santé', 'maladie'],
    audio: 'Mido_nawni'
  },
  {
    original: "Hibhè nawni sèdha",
    french: "Elle est un peu souffrante",
    english: "She's feeling a bit unwell",
    words: ["Hibhè", "nawni", "sèdha"],
    category: 'Urgences et santé',
    tags: ['santé', 'maladie', 'féminin'],
    audio: 'Hibhe_nawni_sedha'
  },
  {
    original: "Ko hombo nawni",
    french: "Qui est malade ?",
    english: "Who is sick?",
    words: ["Ko", "hombo", "nawni"],
    category: 'Urgences et santé',
    tags: ['santé', 'question'],
    audio: 'Ko_hombo_nawni'
  },
  {
    original: "baawo an ngon no muusude lan",
    french: "J'ai mal à la tête",
    english: "I have a headache",
    words: ["baawo", "an", "ngon", "no", "muusude", "lan"],
    category: 'Urgences et santé',
    tags: ['santé', 'douleur', 'tête'],
    audio: 'baawo_an_ngon_no_muusude_lan'
  },
  {
    original: "Yiiye an den no muusude lan",
    french: "J'ai mal au dents",
    english: "I have a toothache",
    words: ["Yiiye", "an", "den", "no", "muusude", "lan"],
    category: 'Urgences et santé',
    tags: ['santé', 'douleur', 'dents']
  },
  {
    original: "Gite an den no muusude lan",
    french: "J'ai mal au yeux",
    english: "My eyes hurt",
    words: ["Gite", "an", "den", "no", "muusude", "lan"],
    category: 'Urgences et santé',
    tags: ['santé', 'douleur', 'yeux'],
    audio: 'Gite_an_den_no_muusude_lan'
  },
  {
    original: "Baawo an ngon no muusude lan",
    french: "J'ai mal au dos",
    english: "My back hurts",
    words: ["Baawo", "an", "ngon", "no", "muusude", "lan"],
    category: 'Urgences et santé',
    tags: ['santé', 'douleur', 'dos'],
    audio: 'Baawo_an_ngon_no_muusude_lan'
  },
  {
    original: "Ko waggoutou hombo nii",
    french: "Quelle heure est-il ?",
    english: "What time is it?",
    words: ["Ko", "waggoutou", "hombo", "nii"],
    category: 'Vie quotidienne',
    tags: ['temps', 'heure', 'question'],
    audio: 'Ko_waggoutou_hombo_nii'
  },
  {
    original: "Bee yahaa ton jooni",
    french: "Il faut que tu y ailles tout de suite",
    english: "You need to go there right away",
    words: ["Bee", "yahaa", "ton", "jooni"],
    category: 'Vie quotidienne',
    tags: ['obligation', 'urgence'],
    audio: 'Bee_yahaa_ton_jooni'
  },
  {
    original: "Min ndharou film",
    french: "On a regardé un film",
    english: "We watched a movie",
    words: ["Min", "ndharou", "film"],
    category: 'Loisirs et culture',
    tags: ['loisirs', 'cinéma', 'divertissement'],
    audio: 'Min_ndharou_film'
  },

   {
    original: "Lan_yidhi_mignan_an_Moussa",
    french: "J\'aime mon frere Moussa",
    english: "I love my brother Moussa",
    words: ["Lan", "yidhi","mignan","an","Moussa"],
    category: 'Famille et relations',
    tags: ['famille', 'immédiat'],
    audio: 'Lan_yidhi_mignan_an_Moussa'
  },

    {
    original: "Lan_yidhou_ma",
    french: "Je t'\aime",
    english: "I love you",
    words: ["Lan", "yidhi","ma"],
    category: 'Famille et relations',
    tags: ['famille', 'immédiat'],
    audio: 'Lan_yidhou_ma'
  },


];

// Fonctions utilitaires
export const getSentencesByCategory = (category: keyof typeof SENTENCE_CATEGORIES) =>
  sentencesToConstruct.filter(sentence => sentence.category === category);

export const getSentencesByTags = (tags: string[]) =>
  sentencesToConstruct.filter(sentence =>
    tags.some(tag => sentence.tags.includes(tag))
  );

export const getCategories = () => SENTENCE_CATEGORIES;

export default sentencesToConstruct;
